// Utilities
import type { AppAd } from '@/contentTypes';
import categories from '@/configs/categories';
import { defineStore } from 'pinia';
import { CACHE_CONSTANTS } from '@/configs/constants';

export const useAppStore = defineStore('app', {
    state: () => ({
        _isSidebarOpen: false,
        postsLastFetchedAt: 0, // Timestamp of the last fetch
        alreadyRefreshed: false,
        requestsMade: 0, // Count of requests made
        postsTotals: {} as Record<string, number>, // Store totals for each post type
        openPostUrl: '', // URL of the post currently open
        singleAdIndex: {} as { [key: string]: number }, // Index for single ad display, keyed by section
        ads: [] as AppAd[], // Store all active ads
        deepLink: false,
        invalidDeepLink: false, // Flag to indicate if the deep link is invalid
        isFirstVisit: true, // Track if this is the user's first visit
        _showPWAInstallBanner: false, // Show PWA install banner
        pwaInstallDismissed: false, // Track if user dismissed PWA install
        appBuildNumber: 0,
    }),
    getters: {
        isSidebarOpen(): boolean {
            return this._isSidebarOpen;
        },
        getPostsLastFetched(): number {
            return this.postsLastFetchedAt;
        },
        getRequestsMade(): number {
            return this.requestsMade;
        },
        getPostsTotals(): Record<string, number> {
            return this.postsTotals;
        },
        getPostsTotalByCategory(): (categoryId: number | string) => number {
            return (categoryId) => {
                return this.postsTotals[`_${categoryId}`] || 0;
            };
        },
        getOpenPostUrl(): string {
            return this.openPostUrl;
        },
        isDeepLink(): boolean {
            return this.deepLink;
        },
        isInvalidDeepLink(): boolean {
            return this.invalidDeepLink;
        },
        getIsFirstVisit(): boolean {
            return this.isFirstVisit;
        },
        getShowPWAInstallBanner(): boolean {
            return this._showPWAInstallBanner;
        },
        getPWAInstallDismissed(): boolean {
            return this.pwaInstallDismissed;
        },
        getAlreadyRefreshed(): boolean {
            return this.alreadyRefreshed;
        },
        getAds(): AppAd[] {
            return this.ads;
        },
        getAdsBySection(): (section: string) => AppAd[] {
            return (section) => {
                const normalizedSection = section.toLowerCase();
                const cat = categories.find((c) => c.value.toLowerCase() === normalizedSection);
                if (!cat) {
                    return this.ads;
                }
                return this.ads.filter((ad) => {
                    if (!ad.categoryWpIds || ad.categoryWpIds.length === 0) {
                        return true;
                    }
                    return ad.categoryWpIds.includes(cat.wordpressId);
                });
            };
        },
        getAppBuildNumber(): number {
            return this.appBuildNumber;
        },
    },
    actions: {
        setIsSidebarOpen(value: boolean) {
            this._isSidebarOpen = value;
        },
        setPostsLastFetched(timestamp: number) {
            this.postsLastFetchedAt = timestamp;
        },
        incrementRequestsMade() {
            this.requestsMade++;
        },
        setPostsTotals(totals: Record<string, number>) {
            this.postsTotals = totals;
        },
        setPostsTotalByType(type: string, total: number) {
            this.postsTotals[type] = total;
        },
        setOpenPostUrl(url: string) {
            this.openPostUrl = url;
        },
        setAlreadyRefreshed(value: boolean) {
            this.alreadyRefreshed = value;
        },
        async setAds(ads: AppAd[]) {
            this.ads = ads;
            const cache = await caches.open(CACHE_CONSTANTS.CACHE_NAMES.ADS);
            const incomingImgUrls = ads.map((ad) => ad.img).filter(Boolean);

            const keys = await cache.keys();
            for (const request of keys) {
                if (!incomingImgUrls.includes(request.url)) {
                    await cache.delete(request);
                }
            }

            const cachePromises = ads
                .filter((ad) => ad.img)
                .map(async (ad) => {
                    const cached = await cache.match(ad.img);
                    if (!cached) {
                        try {
                            const response = await fetch(ad.img, { mode: 'no-cors' });
                            await cache.put(ad.img, response);
                        } catch (err) {
                            console.warn(`Failed to cache ad image: ${ad.img}`, err);
                        }
                    }
                });
            await Promise.all(cachePromises).catch((err) => console.warn('Error caching ads:', err));
        },
        setDeepLink(value: boolean) {
            this.deepLink = value;
        },
        setInvalidDeepLink(value: boolean) {
            this.invalidDeepLink = value;
        },
        setIsFirstVisit(value: boolean) {
            this.isFirstVisit = value;
        },
        setShowPWAInstallBanner(value: boolean) {
            this._showPWAInstallBanner = value;
        },
        setPWAInstallDismissed(value: boolean) {
            this.pwaInstallDismissed = value;
        },
        getSingleAdIndex(destinationSection: string): number {
            if (typeof this.singleAdIndex[destinationSection] === 'undefined') {
                this.singleAdIndex[destinationSection] = 0;
            } else {
                this.singleAdIndex[destinationSection]++;
            }

            const adsForSection = this.getAdsBySection(destinationSection);
            if (this.singleAdIndex[destinationSection] >= adsForSection.length) {
                this.singleAdIndex[destinationSection] = 0;
            }

            return this.singleAdIndex[destinationSection];
        },
        setAppBuildNumber(buildNumber: number) {
            this.appBuildNumber = buildNumber;
        },
    },
    persist: {
        pick: ['postsLastFetchedAt', 'ads', 'isFirstVisit', 'pwaInstallDismissed', '_showPWAInstallBanner', 'alreadyRefreshed', 'singleAdIndex', 'appVersion'],
    },
});
